import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import {
  ENABLE_NFC_OP_PARAM,
  ENABLE_NFC_OPENER_PARAM,
  NFC_ENABLE_RESULT_TYPE,
  PROTOCOL_VERSION,
} from '@/utils/paymentHandlerBridge';

// If this tab is never focused within this window of mounting, assume it opened as
// an orphan background tab — a known Chrome behavior when window.open() runs without
// transient activation — and close it so it doesn't linger. A tab the user is
// actually looking at clears the timer on its first focus/visibility event.
const ORPHAN_CLOSE_TIMEOUT_MS = 20_000;

// Minimal shape of the Web NFC reader we use. The repo has no w3c-web-nfc types and
// detects support with `'NDEFReader' in window` elsewhere; we only need scan(), the
// call that triggers the browser's NFC permission prompt.
interface NdefReaderLike {
  scan: (options?: { signal?: AbortSignal }) => Promise<void>;
}
type NdefReaderCtor = new () => NdefReaderLike;

function getNdefReaderCtor(): NdefReaderCtor | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { NDEFReader?: NdefReaderCtor }).NDEFReader;
}

type Status = 'idle' | 'requesting' | 'denied' | 'unsupported' | 'done';

/**
 * Dedicated NFC-permission page — its ONLY job is to obtain the Web NFC grant for this
 * origin. Reached via the top-level `/enable-nfc` route, OUTSIDE the /3ds and /ul
 * flows: it has no session and renders no transaction.
 *
 * Chrome 149+ (the CVE-2026-11108 fix) no longer shows the NFC prompt inside a Payment
 * Handler window, and window.open()/clients.openWindow() are blocked from one. So the
 * invoker opens THIS page as a real top-level tab, where the user taps "Allow NFC
 * access" to call `NDEFReader.scan()` — Web NFC REQUIRES a user gesture, so it must come
 * from the click, never on mount. Once granted, the page reports to window.opener (the
 * invoker) and auto-closes; the invoker then re-presents a fresh PH window. See
 * docs/PAYMENT_HANDLER_NFC_PERMISSION_FLOW.md.
 */
export function EnableNfc() {
  const [status, setStatus] = useState<Status>('idle');
  // Guard so we report exactly one outcome to the invoker.
  const reportedRef = useRef(false);

  // The unique operation id + expected opener origin the invoker put on the URL, so
  // the result message can be validated rather than trusted. Absent when the page
  // is opened directly (no invoker) — then we fall back to '*' / no op.
  const { op, openerOrigin } = useMemo(() => {
    const params = new URLSearchParams(
      typeof window === 'undefined' ? '' : window.location.search,
    );
    return {
      op: params.get(ENABLE_NFC_OP_PARAM),
      openerOrigin: params.get(ENABLE_NFC_OPENER_PARAM),
    };
  }, []);

  // Report the outcome to the invoker (window.opener) and auto-close. No ack is
  // needed: a postMessage to the opener is delivered even as this tab tears down,
  // and the invoker also polls window.closed as a fallback. Targets the known
  // opener origin when the invoker supplied one, else '*'.
  const finish = (granted: boolean, code?: string) => {
    if (reportedRef.current) return;
    reportedRef.current = true;
    (window.opener as Window | null)?.postMessage(
      {
        protocol: PROTOCOL_VERSION,
        type: NFC_ENABLE_RESULT_TYPE,
        op,
        granted,
        code,
      },
      openerOrigin || '*',
    );
    setStatus('done');
    window.close();
  };

  // The button handler. scan() REQUIRES transient activation to show the prompt, so it
  // must run from the click — on mount it is silently blocked and no prompt appears. It
  // resolves once permission is granted and scanning starts (we don't wait for a card
  // tap), and rejects if the user blocks the prompt or NFC is unavailable.
  const requestNfcPermission = async () => {
    const Ctor = getNdefReaderCtor();
    if (!Ctor) {
      setStatus('unsupported');
      return;
    }
    setStatus('requesting');
    try {
      await new Ctor().scan();
      finish(true);
    } catch {
      // Denied, dismissed, or NFC unavailable — permission wasn't granted. Stay on
      // the page so the user can try again (or cancel); don't report, so the invoker
      // can still receive a later grant or detect the tab closing.
      setStatus('denied');
    }
  };

  // If the grant already exists (e.g. allowed earlier), there's nothing to prompt —
  // report and close straight away. permissions.query() never shows a prompt; the
  // Promise.resolve() wrapper turns a missing/throwing permissions API into a caught
  // rejection, so a browser without it just falls through to the button.
  useEffect(() => {
    let cancelled = false;
    Promise.resolve()
      .then(() =>
        navigator.permissions.query({ name: 'nfc' as PermissionName }),
      )
      .then((result) => {
        if (!cancelled && result.state === 'granted') finish(true);
      })
      .catch(() => {
        /* no grant / no permissions API — leave the button for the user */
      });
    return () => {
      cancelled = true;
    };
    // Mount only; finish is a one-shot guarded closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Orphan-tab self-close.
  useEffect(() => {
    if (document.visibilityState === 'visible' && document.hasFocus()) return;
    const clear = () => {
      if (document.visibilityState === 'visible' || document.hasFocus()) {
        document.removeEventListener('visibilitychange', clear);
        window.removeEventListener('focus', clear);
        clearTimeout(timer);
      }
    };
    document.addEventListener('visibilitychange', clear);
    window.addEventListener('focus', clear);
    const timer = setTimeout(() => {
      document.removeEventListener('visibilitychange', clear);
      window.removeEventListener('focus', clear);
      window.close();
    }, ORPHAN_CLOSE_TIMEOUT_MS);
    return () => {
      document.removeEventListener('visibilitychange', clear);
      window.removeEventListener('focus', clear);
      clearTimeout(timer);
    };
  }, []);

  if (status === 'done') {
    // Reached only when window.close() was a no-op — the page was opened directly
    // rather than by the invoker. Give the user a way out.
    return (
      <div style={S.page}>
        <div style={S.card} data-testid="EnableNfc--Done">
          <h1 style={S.title}>NFC enabled</h1>
          <p style={S.body}>You can close this tab and return to continue.</p>
          <button
            type="button"
            style={S.primary}
            onClick={() => window.close()}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (status === 'unsupported') {
    return (
      <div style={S.page}>
        <div style={S.card} data-testid="EnableNfc--Unsupported">
          <h1 style={S.title}>NFC isn’t available</h1>
          <p style={S.body}>
            This device or browser doesn’t support Web NFC. Open this page in
            Chrome on an NFC-capable Android phone.
          </p>
          <button
            type="button"
            style={S.cancel}
            onClick={() => finish(false, 'NOT_SUPPORTED')}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.card} data-testid="EnableNfc">
        <h1 style={S.title}>Allow NFC access</h1>
        <p style={S.body}>
          To tap your card, this site needs permission to use NFC. Tap the
          button below, then choose <strong>Allow</strong> when your browser
          asks.
        </p>
        {status === 'denied' && (
          <p style={S.error} data-testid="EnableNfc--Denied">
            NFC access wasn’t granted. Tap again, or allow NFC for this site in
            your browser settings.
          </p>
        )}
        <button
          type="button"
          style={S.primary}
          onClick={requestNfcPermission}
          disabled={status === 'requesting'}
          data-testid="EnableNfc--Allow"
        >
          {status === 'requesting'
            ? 'Waiting for permission…'
            : 'Allow NFC access'}
        </button>
        <button
          type="button"
          style={S.cancel}
          onClick={() => finish(false, 'USER_CANCELLED')}
          data-testid="EnableNfc--Cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

const S: Record<string, CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    background: '#f5f6f8',
    color: '#1a1a2e',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    maxWidth: 360,
    width: '100%',
    background: '#fff',
    borderRadius: 16,
    padding: 28,
    textAlign: 'center',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
  },
  title: { fontSize: 20, margin: '0 0 10px' },
  body: { fontSize: 15, lineHeight: 1.5, color: '#4b5563', margin: '0 0 20px' },
  error: { fontSize: 14, color: '#b00020', margin: '0 0 16px' },
  primary: {
    width: '100%',
    padding: '14px 16px',
    border: 0,
    borderRadius: 10,
    background: '#013d5b',
    color: '#fff',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
  },
  cancel: {
    width: '100%',
    padding: '12px 16px',
    marginTop: 10,
    border: 0,
    borderRadius: 10,
    background: 'transparent',
    color: '#6b7280',
    fontSize: 15,
    cursor: 'pointer',
  },
};
